import { getFireworksPricing } from "../fireworks/pricing.js";
import { getGeminiImagePricing, getGeminiProPricing } from "../google/pricing.js";
import {
  getOpenAiImagePricing,
  getOpenAiPricing,
  type OpenAiImagePriceResolution,
  type OpenAiImagePricing,
} from "../openai/pricing.js";

export type LlmUsageTokens = {
  readonly promptTokens?: number;
  readonly promptTextTokens?: number;
  readonly promptImageTokens?: number;
  readonly cachedTokens?: number;
  readonly responseTokens?: number;
  readonly responseTextTokens?: number;
  readonly responseImageTokens?: number;
  readonly thinkingTokens?: number;
  readonly totalTokens?: number;
  readonly toolUsePromptTokens?: number;
};

function resolveUsageNumber(value: number | undefined): number {
  if (typeof value === "number" && Number.isFinite(value)) {
    return Math.max(0, value);
  }
  return 0;
}

export function estimateCallCostUsd({
  modelId,
  pricingModelId,
  tokens,
  responseImages,
  imageSize,
  imageQuality,
}: {
  modelId: string;
  pricingModelId?: string;
  tokens: LlmUsageTokens | undefined;
  responseImages: number;
  imageSize?: string;
  imageQuality?: string;
}): number {
  const pricingModelIds = resolvePricingModelIds(modelId, pricingModelId);
  const openAiImagePricing = resolvePricing(pricingModelIds, getOpenAiImagePricing);
  if (openAiImagePricing) {
    return estimateOpenAiImageCostUsd({
      pricing: openAiImagePricing,
      responseImages,
      imageSize,
      imageQuality,
    });
  }

  if (!tokens) {
    return 0;
  }
  const promptTokens = resolveUsageNumber(tokens.promptTokens);
  const cachedTokens = resolveUsageNumber(tokens.cachedTokens);
  const responseTokens = resolveUsageNumber(tokens.responseTokens);
  const responseImageTokens = resolveUsageNumber(tokens.responseImageTokens);
  const thinkingTokens = resolveUsageNumber(tokens.thinkingTokens);
  const toolUsePromptTokens = resolveUsageNumber(tokens.toolUsePromptTokens);
  const promptTokenTotal = promptTokens + toolUsePromptTokens;
  const nonCachedPrompt = Math.max(0, promptTokenTotal - cachedTokens);

  const imagePreviewPricing = resolvePricing(pricingModelIds, getGeminiImagePricing);
  if (imagePreviewPricing) {
    const resolvedImageSize =
      imageSize && imagePreviewPricing.imagePrices[imageSize] ? imageSize : "2K";
    const imageRate = imagePreviewPricing.imagePrices[resolvedImageSize] ?? 0;
    const tokensPerImage =
      imagePreviewPricing.outputImageRate > 0 ? imageRate / imagePreviewPricing.outputImageRate : 0;
    let responseTextForPricing = Math.max(0, responseTokens - responseImageTokens);
    let imageTokensForPricing = responseImageTokens;
    if (imageTokensForPricing <= 0 && responseImages > 0 && tokensPerImage > 0) {
      const estimatedImageTokens = responseImages * tokensPerImage;
      imageTokensForPricing = estimatedImageTokens;
      if (responseTextForPricing >= estimatedImageTokens) {
        responseTextForPricing -= estimatedImageTokens;
      }
    }
    const textOutputCost =
      (responseTextForPricing + thinkingTokens) * imagePreviewPricing.outputTextRate;
    const inputCost = nonCachedPrompt * imagePreviewPricing.inputRate;
    const cachedCost = cachedTokens * imagePreviewPricing.cachedRate;
    const imageOutputCost = imageTokensForPricing * imagePreviewPricing.outputImageRate;
    return inputCost + cachedCost + textOutputCost + imageOutputCost;
  }

  const geminiPricing = resolvePricing(pricingModelIds, getGeminiProPricing);
  if (geminiPricing) {
    const useHighTier = promptTokenTotal > geminiPricing.threshold;
    const inputRate = useHighTier ? geminiPricing.inputRateHigh : geminiPricing.inputRateLow;
    const cachedRate = useHighTier ? geminiPricing.cachedRateHigh : geminiPricing.cachedRateLow;
    const outputRate = useHighTier ? geminiPricing.outputRateHigh : geminiPricing.outputRateLow;
    const inputCost = nonCachedPrompt * inputRate;
    const cachedCost = cachedTokens * cachedRate;
    const outputTokens = responseTokens + thinkingTokens;
    const outputCost = outputTokens * outputRate;
    return inputCost + cachedCost + outputCost;
  }

  const fireworksPricing = resolvePricing(pricingModelIds, getFireworksPricing);
  if (fireworksPricing) {
    const inputCost = nonCachedPrompt * fireworksPricing.inputRate;
    const cachedCost = cachedTokens * fireworksPricing.cachedRate;
    const outputTokens = responseTokens + thinkingTokens;
    const outputCost = outputTokens * fireworksPricing.outputRate;
    return inputCost + cachedCost + outputCost;
  }

  const openAiPricing = resolvePricing(pricingModelIds, getOpenAiPricing);
  if (openAiPricing) {
    const inputCost = nonCachedPrompt * openAiPricing.inputRate;
    const cachedCost = cachedTokens * openAiPricing.cachedRate;
    const outputTokens = responseTokens + thinkingTokens;
    const outputCost = outputTokens * openAiPricing.outputRate;
    return inputCost + cachedCost + outputCost;
  }

  return 0;
}

function resolvePricingModelIds(modelId: string, pricingModelId: string | undefined): string[] {
  if (pricingModelId && pricingModelId !== modelId) {
    return [pricingModelId, modelId];
  }
  return [modelId];
}

function resolvePricing<T>(
  modelIds: readonly string[],
  resolve: (modelId: string) => T | undefined,
): T | undefined {
  for (const modelId of modelIds) {
    const pricing = resolve(modelId);
    if (pricing) {
      return pricing;
    }
  }
  return undefined;
}

function estimateOpenAiImageCostUsd({
  pricing,
  responseImages,
  imageSize,
  imageQuality,
}: {
  pricing: OpenAiImagePricing;
  responseImages: number;
  imageSize?: string;
  imageQuality?: string;
}): number {
  if (responseImages <= 0) {
    return 0;
  }
  const quality =
    imageQuality === "low" || imageQuality === "medium" || imageQuality === "high"
      ? imageQuality
      : pricing.defaultQuality;
  const resolution = resolveOpenAiImagePriceResolution(imageSize) ?? pricing.defaultResolution;
  return responseImages * pricing.imagePrices[quality][resolution];
}

function resolveOpenAiImagePriceResolution(
  imageSize: string | undefined,
): OpenAiImagePriceResolution | undefined {
  if (imageSize === "1024x1024" || imageSize === "1024x1536" || imageSize === "1536x1024") {
    return imageSize;
  }
  if (!imageSize || imageSize === "auto") {
    return undefined;
  }
  const match = /^(\d+)x(\d+)$/.exec(imageSize);
  if (!match) {
    return undefined;
  }
  const width = Number(match[1]);
  const height = Number(match[2]);
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    return undefined;
  }
  if (width === height) {
    return "1024x1024";
  }
  return width > height ? "1536x1024" : "1024x1536";
}
